import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { AuthProvider } from '@usoftware/auth-react'
import { OrgContextProvider } from './contexts'
import { TooltipProvider } from './components/ui'
import App from './App'
import './styles/globals.css'

const CLIENT_ID = import.meta.env.VITE_CLIENT_ID || 'account-dashboard'

/**
 * Extract org slug from hostname and build dynamic URLs
 * Production: account.{slug}.u.software -> core.{slug}.u.software, api.{slug}.u.software
 * Fly.io: usoftware-account.fly.dev -> use env vars or testtes fallback
 * Development: localhost -> use env vars
 */
function getAuthConfig() {
  const hostname = window.location.hostname

  // Production pattern: account.{slug}.u.software
  const prodMatch = hostname.match(/^account\.(.+)\.u\.software$/)
  if (prodMatch) {
    const orgSlug = prodMatch[1]
    return {
      coreUrl: `https://core.${orgSlug}.u.software`,
      apiUrl: `https://api.${orgSlug}.u.software`,
    }
  }

  // Alternative pattern: {slug}.account.u.software
  const altMatch = hostname.match(/^(.+)\.account\.u\.software$/)
  if (altMatch) {
    const orgSlug = altMatch[1]
    return {
      coreUrl: `https://${orgSlug}.core.u.software`,
      apiUrl: `https://${orgSlug}.api.u.software`,
    }
  }

  // Main u.software (no org): account.u.software
  if (hostname === 'account.u.software') {
    return {
      coreUrl: 'https://core.u.software',
      apiUrl: 'https://api.u.software',
    }
  }

  // Fly.io deployment: usoftware-account.fly.dev
  if (hostname === 'usoftware-account.fly.dev') {
    return {
      coreUrl: 'https://usoftware-core.fly.dev',
      apiUrl: 'https://usoftware-api.fly.dev',
    }
  }

  // Development fallback - only for actual localhost
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return {
      coreUrl: import.meta.env.VITE_CORE_URL || 'http://localhost:3000',
      apiUrl: import.meta.env.VITE_API_URL || 'http://localhost:8080',
    }
  }

  // Unknown domain - try to infer from pattern or use fly.dev defaults
  // This prevents localhost fallback for unrecognized production domains
  console.warn(`Unknown hostname: ${hostname}, using fly.dev defaults`)
  return {
    coreUrl: 'https://usoftware-core.fly.dev',
    apiUrl: 'https://usoftware-api.fly.dev',
  }
}

const { coreUrl, apiUrl } = getAuthConfig()

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <BrowserRouter>
      <OrgContextProvider>
        <TooltipProvider>
          <AuthProvider
            clientId={CLIENT_ID}
            redirectUri={`${window.location.origin}/callback`}
            baseUrl={coreUrl}
            apiUrl={apiUrl}
            scopes={['openid', 'profile', 'email']}
            onError={(error: Error) => {
              console.error('Auth error:', error)
            }}
          >
            <App />
          </AuthProvider>
        </TooltipProvider>
      </OrgContextProvider>
    </BrowserRouter>
  </React.StrictMode>,
)
